import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Footer from '../Footer';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { Step1ProfileType } from './create-profile/Step1ProfileType';
import { Step2BasicInfo } from './create-profile/Step2BasicInfo';
import { Step3SkillsCerts } from './create-profile/Step3SkillsCerts';
import { Step4ExpEdu } from './create-profile/Step4ExpEdu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const CreateProfilePage = () => {
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated, loading: authLoading } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    profile_type: '',
    name: '',
    email: '',
    profile_photo: '',
    phone: '',
    whatsapp: '',
    headline: '',
    bio: '',
    location: '',
    website: '',
    linkedin: '',
    skills: [],
    certifications: [],
    experiences: [],
    education: [],
    company_size: '',
    industry: '',
    services: []
  });

  const [newSkill, setNewSkill] = useState('');
  const [newService, setNewService] = useState('');

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated || !currentUser) {
      toast.error('Faça login para criar seu perfil');
      navigate('/connect');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      name: currentUser.name || '',
      email: currentUser.email || ''
    }));
  }, [navigate, currentUser, isAuthenticated, authLoading]);

  const handleAddSkill = () => {
    if (newSkill && !formData.skills.includes(newSkill)) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill]
      }));
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const handleAddService = () => {
    if (newService && !formData.services.includes(newService)) {
      setFormData(prev => ({
        ...prev,
        services: [...prev.services, newService]
      }));
      setNewService('');
    }
  };

  const handleAddCertification = () => {
    setFormData(prev => ({
      ...prev,
      certifications: [...prev.certifications, { _key: uid(), name: '', issuer: '', issue_date: '' }]
    }));
  };

  const handleUpdateCertification = (index, field, value) => {
    const updated = [...formData.certifications];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, certifications: updated }));
  };

  const handleRemoveCertification = (index) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const handleAddExperience = () => {
    setFormData(prev => ({
      ...prev,
      experiences: [...prev.experiences, { 
        _key: uid(), title: '', company: '', location: '', 
        start_date: '', end_date: '', is_current: false, description: ''
      }]
    }));
  };

  const handleUpdateExperience = (index, field, value) => {
    const updated = [...formData.experiences];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, experiences: updated }));
  };

  const handleRemoveExperience = (index) => {
    setFormData(prev => ({
      ...prev,
      experiences: prev.experiences.filter((_, i) => i !== index)
    }));
  };

  const handleAddEducation = () => {
    setFormData(prev => ({
      ...prev,
      education: [...prev.education, { 
        _key: uid(), institution: '', degree: '', field_of_study: '', 
        start_date: '', end_date: ''
      }]
    }));
  };

  const handleUpdateEducation = (index, field, value) => {
    const updated = [...formData.education];
    updated[index][field] = value;
    setFormData(prev => ({ ...prev, education: updated }));
  };

  const handleRemoveEducation = (index) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.profile_type || !formData.name || !formData.email) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        certifications: formData.certifications.map(({ _key, ...rest }) => rest),
        experiences: formData.experiences.map(({ _key, ...rest }) => rest),
        education: formData.education.map(({ _key, ...rest }) => rest),
      };
      const response = await axios.post(
        `${API}/connect/profiles?user_id=${currentUser.id}`,
        payload
      );

      if (response.data.success) {
        toast.success('Perfil criado com sucesso! 🎉');
        navigate(`/connect/profile/${response.data.profile_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Formato inválido. Envie JPG, PNG ou WEBP.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5 MB.');
      return;
    }

    // Recomendação de dimensões mínimas (não bloqueia)
    const img = new Image();
    img.onload = () => {
      if (img.width < 400 || img.height < 400) {
        toast.warning('Recomendamos imagens de no mínimo 400×400px para melhor qualidade.');
      }
    };
    img.src = URL.createObjectURL(file);

    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/connect/upload-image`, fd);
      setFormData(prev => ({ ...prev, profile_photo: res.data.url }));
      toast.success('Imagem enviada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar imagem');
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <section className="pt-28 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Progress */}
          <div className="flex items-center justify-between mb-8">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-gray-500'
                }`}>
                  {step > s ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 4 && (
                  <div className={`w-16 md:w-24 h-1 mx-2 ${step > s ? 'bg-blue-600' : 'bg-zinc-800'}`} />
                )}
              </div>
            ))}
          </div>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6 md:p-8">
              {step === 1 && <Step1ProfileType formData={formData} setFormData={setFormData} />}
              {step === 2 && (
                <Step2BasicInfo
                  formData={formData}
                  setFormData={setFormData}
                  fileInputRef={fileInputRef}
                  uploadingPhoto={uploadingPhoto}
                  handlePhotoUpload={handlePhotoUpload}
                />
              )}
              {step === 3 && (
                <Step3SkillsCerts
                  formData={formData}
                  setFormData={setFormData}
                  newSkill={newSkill}
                  setNewSkill={setNewSkill}
                  handleAddSkill={handleAddSkill}
                  handleRemoveSkill={handleRemoveSkill}
                  handleAddCertification={handleAddCertification}
                  handleUpdateCertification={handleUpdateCertification}
                  handleRemoveCertification={handleRemoveCertification}
                />
              )}
              {step === 4 && (
                <Step4ExpEdu
                  formData={formData}
                  handleAddExperience={handleAddExperience}
                  handleUpdateExperience={handleUpdateExperience}
                  handleRemoveExperience={handleRemoveExperience}
                  handleAddEducation={handleAddEducation}
                  handleUpdateEducation={handleUpdateEducation}
                  handleRemoveEducation={handleRemoveEducation}
                />
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-8 pt-6 border-t border-zinc-800">
                <Button
                  variant="outline"
                  onClick={() => step > 1 ? setStep(step - 1) : navigate('/connect')}
                  className="border-zinc-700 text-gray-300"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {step > 1 ? 'Voltar' : 'Cancelar'}
                </Button>

                {step < 4 ? (
                  <Button
                    onClick={() => setStep(step + 1)}
                    disabled={step === 1 && !formData.profile_type}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="next-step-btn"
                  >
                    Próximo
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="create-profile-submit"
                  >
                    {loading ? 'Criando...' : 'Criar Perfil'}
                    <Check className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CreateProfilePage;
